/**
 * Local Storage Cache Utilities for React Query
 * Provides persistent caching with intelligent storage management
 */

// Storage keys
const STORAGE_KEYS = {
  REACT_QUERY_CACHE: 'rq-cache',
  CACHE_METADATA: 'rq-cache-meta',
  STORAGE_USAGE: 'rq-storage-usage',
} as const;

// Configuration
const CACHE_CONFIG = {
  MAX_AGE: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
  MAX_STORAGE_SIZE: 5 * 1024 * 1024, // 5MB limit
  COMPRESSION_THRESHOLD: 1024, // Compress data larger than 1KB
  CLEANUP_INTERVAL: 60 * 60 * 1000, // Cleanup every hour
} as const;

// Types
interface CacheMetadata {
  timestamp: number;
  version: string;
  size: number;
  queryKeys: string[];
}

interface StorageItem<T = any> {
  data: T;
  metadata: {
    timestamp: number;
    version: string;
    queryKey: string;
    compressed: boolean;
  };
}

export interface LocalStorageCacheEntry<T = any> {
  data: T;
  metadata: {
    timestamp: number;
    version: string;
    queryKey: string;
    compressed: boolean;
  };
}

/**
 * Check if localStorage is available and functional
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get current localStorage usage in bytes
 */
export const getStorageUsage = (): number => {
  if (!isLocalStorageAvailable()) return 0;
  
  let totalSize = 0;
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      totalSize += localStorage[key].length + key.length;
    }
  }
  return totalSize;
};

/**
 * Simple compression using JSON.stringify with reduced precision
 */
const compressData = (data: any): string => {
  try {
    // Remove unnecessary precision from numbers and compress JSON
    const compressed = JSON.stringify(data, (key, value) => {
      if (typeof value === 'number' && !Number.isInteger(value)) {
        return Math.round(value * 100) / 100; // Round to 2 decimal places
      }
      return value;
    });
    return compressed;
  } catch {
    return JSON.stringify(data);
  }
};

/**
 * Decompress data
 */
const decompressData = (compressedData: string): any => {
  try {
    return JSON.parse(compressedData);
  } catch {
    return null;
  }
};

/**
 * Clean up expired cache entries
 */
export const cleanupExpiredCache = (): void => {
  if (!isLocalStorageAvailable()) return;

  const now = Date.now();
  const keysToRemove: string[] = [];

  // Check all React Query cache keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('rq-')) {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const parsed: StorageItem = JSON.parse(item);
          if (now - parsed.metadata.timestamp > CACHE_CONFIG.MAX_AGE) {
            keysToRemove.push(key);
          }
        }
      } catch {
        // Invalid item, mark for removal
        keysToRemove.push(key);
      }
    }
  }

  // Remove expired items
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });

  console.log(`Cleaned up ${keysToRemove.length} expired cache entries`);
};

/**
 * Create a storage key for a query
 */
const createStorageKey = (queryKey: unknown[]): string => {
  const keyString = JSON.stringify(queryKey);
  return `rq-${btoa(keyString).replace(/[+/=]/g, '')}`; // Base64 encode and remove special chars
};

/**
 * Save data to localStorage with metadata
 */
export const saveToCache = <T>(queryKey: unknown[], data: T): boolean => {
  if (!isLocalStorageAvailable()) return false;

  try {
    const storageKey = createStorageKey(queryKey);
    const dataString = compressData(data);
    const compressed = dataString.length > CACHE_CONFIG.COMPRESSION_THRESHOLD;
    
    const item: StorageItem<T> = {
      data,
      metadata: {
        timestamp: Date.now(),
        version: '1.0',
        queryKey: JSON.stringify(queryKey),
        compressed,
      },
    };

    const itemString = JSON.stringify(item);
    
    // Check if we have enough space
    const currentUsage = getStorageUsage();
    const itemSize = itemString.length;
    
    if (currentUsage + itemSize > CACHE_CONFIG.MAX_STORAGE_SIZE) {
      // Try to free up space by cleaning expired items
      cleanupExpiredCache();
      
      // Check again
      if (getStorageUsage() + itemSize > CACHE_CONFIG.MAX_STORAGE_SIZE) {
        console.warn('localStorage quota exceeded, skipping cache save');
        return false;
      }
    }

    localStorage.setItem(storageKey, itemString);
    return true;
  } catch (error) {
    console.error('Failed to save to cache:', error);
    return false;
  }
};

/**
 * Retrieve data from localStorage
 */
export const getCacheEntry = <T>(queryKey: unknown[]): LocalStorageCacheEntry<T> | null => {
  if (!isLocalStorageAvailable()) return null;

  try {
    const storageKey = createStorageKey(queryKey);
    const itemString = localStorage.getItem(storageKey);
    
    if (!itemString) return null;

    const item: StorageItem<T> = JSON.parse(itemString);
    
    // Check if data is expired
    const now = Date.now();
    if (now - item.metadata.timestamp > CACHE_CONFIG.MAX_AGE) {
      localStorage.removeItem(storageKey);
      return null;
    }

    return item;
  } catch (error) {
    console.error('Failed to retrieve from cache:', error);
    return null;
  }
};

/**
 * Retrieve data from localStorage
 */
export const getFromCache = <T>(queryKey: unknown[]): T | null => {
  const entry = getCacheEntry<T>(queryKey);
  return entry ? entry.data : null;
};

/**
 * Remove specific item from cache
 */
export const removeFromCache = (queryKey: unknown[]): void => {
  if (!isLocalStorageAvailable()) return;

  try {
    const storageKey = createStorageKey(queryKey);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error('Failed to remove from cache:', error);
  }
};

/**
 * Clear all React Query cache from localStorage
 */
export const clearAllCache = (): void => {
  if (!isLocalStorageAvailable()) return;

  const keysToRemove: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('rq-')) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });

  console.log(`Cleared ${keysToRemove.length} cache entries`);
};

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  if (!isLocalStorageAvailable()) {
    return {
      available: false,
      totalItems: 0,
      totalSize: 0,
      usage: '0%',
      maxSize: CACHE_CONFIG.MAX_STORAGE_SIZE, // Always include maxSize
    };
  }

  let totalItems = 0;
  let totalSize = 0;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('rq-')) {
      totalItems++;
      const item = localStorage.getItem(key);
      if (item) {
        totalSize += item.length + key.length;
      }
    }
  }

  const usage = ((totalSize / CACHE_CONFIG.MAX_STORAGE_SIZE) * 100).toFixed(1);

  return {
    available: true,
    totalItems,
    totalSize,
    usage: `${usage}%`,
    maxSize: CACHE_CONFIG.MAX_STORAGE_SIZE,
  };
};

/**
 * Initialize cache cleanup interval
 */
export const initializeCacheCleanup = (): (() => void) => {
  // Run initial cleanup
  cleanupExpiredCache();

  // Set up periodic cleanup
  const intervalId = setInterval(cleanupExpiredCache, CACHE_CONFIG.CLEANUP_INTERVAL);

  // Return cleanup function
  return () => {
    clearInterval(intervalId);
  };
};
